import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Users,
  ChevronDown,
  LogOut,
  UserCog,
  Building,
  Download,
  Clock,
  MessageSquare,
  MessageSquarePlus,
  PanelLeft
} from 'lucide-react';

import LeadsTable from './components/LeadsTable';
import ChatWidget from './components/ChatWidget';
import GIDashboard from './components/GIDashboard';
import Login from './components/Login';
import Admin from './components/Admin';
import LeadCategoryManagement from './components/LeadCategoryManagement';
import FileUploadManagement from './components/FileUploadManagement';
import OutboundLeadModal from './components/OutboundLeadModal';
import TriggerInboundModal from './components/TriggerInboundModal';
import Monitoring from './components/Monitoring';
import AppointmentsManagement from './components/AppointmentsManagement';
import AppointmentsV2 from './components/AppointmentsV2';
import AppHeader from './components/AppHeader';
import EnterpriseSelector from './components/EnterpriseSelector';

import {
  getLeads,
  getConversationByLeadId,
  deleteLead
} from './services/dataService';

import { getApiBaseUrl, getLeadById, resolveLeadLink, isDevEnvironment } from './services/api';
import { authService } from './services/authService';
import { formatTimeZoneLabel, resolveTimeZone, getTimeZoneColor, toIanaTimeZone } from './services/dateService';
import { leadEventBus } from './services/leadEventBus';

const SIDEBAR_MIN_WIDTH = 240;
const SIDEBAR_MAX_WIDTH = 520;
const SIDEBAR_DEFAULT_WIDTH = 320;

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultStartDate = () => {
  const today = new Date();
  return formatDateForInput(new Date(today.getFullYear(), today.getMonth() - 1, today.getDate()));
};

const getDefaultEndDate = () => formatDateForInput(new Date());

const LiveZoneClock = ({ timeZone, showIcon = true, className = 'inline-flex items-center text-xs text-gray-600' }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);


  if (!timeZone) {
    return null;
  }

  try {
    const ianaTimeZone = toIanaTimeZone(timeZone);
    const time = now.toLocaleTimeString('en-US', {
      timeZone: ianaTimeZone,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    });
    return (
      <span className={className}>
        {showIcon && <Clock size={12} className="mr-1" />}
        {time}
      </span>
    );
  } catch (error) {
    console.error('LiveZoneClock error:', error, 'timeZone:', timeZone, 'ianaTimeZone:', toIanaTimeZone(timeZone));
    return null;
  }
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [openLeadIds, setOpenLeadIds] = useState([]);
  const [lastOpenedLeadId, setLastOpenedLeadId] = useState(null);
  const [widgetWidths, setWidgetWidths] = useState({}); // { leadId: px }
  const [focusedLeadId, setFocusedLeadId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Pagination and filtering state
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [bodyTypeFilter, setBodyTypeFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());

  // Store selection state
  const [enterprises, setEnterprises] = useState([]);
  const [bocs, setBocs] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [selectedBOC, setSelectedBOC] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreAssistant, setSelectedStoreAssistant] = useState(null);
  // Non-fatal message shown in the Store Selection panel (e.g. a store the user
  // can't access, or a USER account with no enterprise assigned yet).
  const [storeAccessNotice, setStoreAccessNotice] = useState(null);
  const [loadingEnterprises, setLoadingEnterprises] = useState(false);
  const [loadingBOCs, setLoadingBOCs] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return localStorage.getItem('leadDashboardSidebarOpen') !== 'false';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('leadDashboardSidebarOpen', String(sidebarOpen));
    } catch {
      /* storage unavailable - not critical */
    }
  }, [sidebarOpen]);

  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const stored = Number(localStorage.getItem('leadDashboardSidebarWidth'));
      if (stored >= SIDEBAR_MIN_WIDTH && stored <= SIDEBAR_MAX_WIDTH) return stored;
    } catch {
      /* storage unavailable - fall through to the default */
    }
    return SIDEBAR_DEFAULT_WIDTH;
  });

  useEffect(() => {
    try {
      localStorage.setItem('leadDashboardSidebarWidth', String(sidebarWidth));
    } catch {
      /* storage unavailable - not critical */
    }
  }, [sidebarWidth]);

  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef({ dragging: false, startX: 0, startWidth: 0, moved: false });

  // The divider doubles as a control: drag it to resize, click it to collapse.
  const handleDividerMouseDown = (e) => {
    if (!sidebarOpen || e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { dragging: true, startX: e.clientX, startWidth: sidebarWidth, moved: false };
    setIsResizing(true);
  };

  const handleDividerClick = () => {
    // Swallow the click that ends a drag so resizing never toggles the panel.
    if (dragRef.current.moved) {
      dragRef.current.moved = false;
      return;
    }
    setSidebarOpen((open) => !open);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const drag = dragRef.current;
      if (!drag.dragging) return;
      const delta = e.clientX - drag.startX;
      if (Math.abs(delta) > 3) drag.moved = true;
      setSidebarWidth(
        Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, drag.startWidth + delta))
      );
    };
    const handleMouseUp = () => {
      if (!dragRef.current.dragging) return;
      dragRef.current.dragging = false;
      setIsResizing(false);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        setSidebarOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const [scrollToBottomOnLoad, setScrollToBottomOnLoad] = useState(false);

  // Leads that have received a new message we haven't shown the user yet
  // (i.e. not the currently open conversation). Drives the unread badge in
  // LeadsTable; cleared as  soon as the lead is selected.
  const [unreadLeadIds, setUnreadLeadIds] = useState(() => new Set());
  const openLeadIdsRef = useRef([]);
  const currentPathRef = useRef(null);
  const deepLinkHandledRef = useRef(false);

  // Ref-cache of each open lead so a widget doesn't briefly go null/undefined
  // while `leads` reloads (entries pruned when the lead's widget is closed).
  const openLeadsCacheRef = useRef(new Map());
  openLeadIds.forEach((id) => {
    const fresh = leads.find((lead) => lead.id === id);
    if (fresh) openLeadsCacheRef.current.set(id, fresh);
  });
  Array.from(openLeadsCacheRef.current.keys()).forEach((id) => {
    if (!openLeadIds.includes(id)) openLeadsCacheRef.current.delete(id);
  });

  const openLeads = openLeadIds
    .map((id) => leads.find((lead) => lead.id === id) || openLeadsCacheRef.current.get(id))
    .filter(Boolean);

  const WIDGET_GAP = 12;
  const DEFAULT_WIDGET_WIDTH = 440;
  const widgetRightOffset = (index) =>
    openLeads
      .slice(0, index)
      .reduce((sum, lead) => sum + (widgetWidths[lead.id] || DEFAULT_WIDGET_WIDTH) + WIDGET_GAP, 0);

  const openLead = useCallback((lead) => {
    if (!lead || !lead.id) return;
    setOpenLeadIds((prev) => {
      if (prev.includes(lead.id)) return prev;
      const next = [...prev, lead.id];
      return next.length > 3 ? next.slice(next.length - 3) : next;
    });
    setLastOpenedLeadId(lead.id);
    setFocusedLeadId(lead.id);
    setScrollToBottomOnLoad(true);
    setTimeout(() => setScrollToBottomOnLoad(false), 100);
  }, []);

  const closeLead = useCallback((leadId) => {
    setOpenLeadIds((prev) => prev.filter((id) => id !== leadId));
    setWidgetWidths((prev) => {
      if (!(leadId in prev)) return prev;
      const next = { ...prev };
      delete next[leadId];
      return next;
    });
  }, []);

  const handleWidgetWidthChange = useCallback((leadId, width) => {
    setWidgetWidths((prev) => {
      if (width == null) {
        if (!(leadId in prev)) return prev;
        const next = { ...prev };
        delete next[leadId];
        return next;
      }
      if (prev[leadId] === width) return prev;
      return { ...prev, [leadId]: width };
    });
  }, []);

  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(null); // stores the leadId to delete
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState(null);
  // Error from a failed appointment cancellation, scoped to the lead it happened on.
  // Deliberately kept separate from `error` below - `error` drives the full-page
  // "Connection Error" screen meant for the app failing to load at all, and a single
  // failed cancel (e.g. Ztime rejecting it) is not that; it should show inline near
  // the Cancel button instead of taking over the whole app.
  const [cancelAppointmentError, setCancelAppointmentError] = useState(null);

  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);

  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [showOutboundModal, setShowOutboundModal] = useState(false);
  const [showTriggerInboundModal, setShowTriggerInboundModal] = useState(false);


  const [awaitingAiReplyByLead, setAwaitingAiReplyByLead] = useState({});
  const clearAwaitingAiReply = useCallback((leadId) => {
    setAwaitingAiReplyByLead((prev) => {
      if (!(leadId in prev)) return prev;
      const next = { ...prev };
      delete next[leadId];
      return next;
    });
  }, []);

  const currentLoadControllerRef = useRef(null);
  const loadLeadsRef = useRef(null);
  const searchQueryRef = useRef(searchQuery);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();

    if (currentUser) {
      setUser(currentUser);
      setIsAuthenticated(true);
      authService.refreshCurrentUser()
        .then((freshUser) => freshUser && setUser(freshUser))
        .catch((err) => console.error('Error refreshing current user:', err));
    }
  }, []);

  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  useEffect(() => {
    return () => {
      currentLoadControllerRef.current?.abort();
      currentLoadControllerRef.current = null;
    };
  }, []);

  // Clear any stale cancel-appointment error once its lead is no longer open
  useEffect(() => {
    setCancelAppointmentError((prev) =>
      prev && !openLeadIds.includes(prev.leadId) ? null : prev
    );
  }, [openLeadIds]);

  // Selection state lives in memory for the whole SPA session, so without this a
  // USER logging in right after an admin (same tab, no reload) would inherit the
  // admin's selected store - typically the default enterprise - and get a 403.
  const resetSelection = () => {
    currentLoadControllerRef.current?.abort();
    currentLoadControllerRef.current = null;
    setSelectedEnterprise(null);
    setSelectedBOC(null);
    setSelectedStore(null);
    setSelectedStoreAssistant(null);
    setEnterprises([]);
    setBocs([]);
    setStores([]);
    setLeads([]);
    setTotalElements(0);
    setTotalPages(0);
    setPage(0);
    setError(null);
    setStoreAccessNotice(null);
  };

  const handleLogin = (userData) => {
    resetSelection();
    setUser({
      username: userData.username,
      role: userData.role,
      enterpriseIdFk: userData.enterpriseIdFk ?? null
    });

    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    authService.logout();
    leadEventBus.disconnect();

    setUser(null);
    setIsAuthenticated(false);

    setOpenLeadIds([]);
    resetSelection();
  };

  const loadLeads = useCallback(async () => {
    const controller = new AbortController();
    const previousController = currentLoadControllerRef.current;

    // Cancel any in-flight request so the latest search/refresh always wins
    if (previousController) {
      previousController.abort();
    }
    currentLoadControllerRef.current = controller;

    try {
      setError(null);
      setStoreAccessNotice(null);

      // Only show loading indicator on initial load
      if (isInitialLoad) {
        setLoadingLeads(true);
      }

      const options = {
        page,
        size,
        ...(bodyTypeFilter && { bodyTypeFilter }),
        ...(activeSearchQuery && { searchQuery: activeSearchQuery }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        signal: controller.signal
      };

      const pageData = await getLeads(selectedStore, options);

      // Ignore responses from a superseded request
      if (currentLoadControllerRef.current !== controller) {
        return;
      }

      // Update pagination metadata on every response so a search that changes
      // only the total count (or returns an empty page) still updates the UI.
      setTotalElements(pageData?.totalElements || 0);
      setTotalPages(pageData?.totalPages || 0);

      // Only update leads array if it actually changed to avoid unnecessary re-renders
      // (preserves focus/scroll position in ChatBox across event-triggered reloads)
      setLeads(prevLeads => {
        const newLeads = pageData?.content || [];
        const leadsChanged =
          prevLeads.length !== newLeads.length ||
          !prevLeads.every((prevLead, idx) => {
            const newLead = newLeads[idx];
            return prevLead && newLead &&
              prevLead.id === newLead.id &&
              JSON.stringify(prevLead) === JSON.stringify(newLead);
          });

        return leadsChanged ? newLeads : prevLeads;
      });
    } catch (err) {
      // Ignore errors from superseded or cancelled requests
      if (currentLoadControllerRef.current !== controller) {
        return;
      }
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        console.log('Leads request cancelled');
        return;
      }
      if (err.type === 'FORBIDDEN') {
        // The selected store belongs to an enterprise this user can't access.
        // Drop the selection instead of taking over the page with "Connection Error".
        console.warn('No access to store', selectedStore, '- clearing selection');
        setSelectedStore(null);
        setSelectedStoreAssistant(null);
        setLeads([]);
        setStoreAccessNotice("You don't have access to that store. Please select a store from your enterprise.");
        return;
      }
      console.error('Error loading leads:', err);
      setError(err.message);
    } finally {
      // Only reset the loading UI if this is still the active request
      if (currentLoadControllerRef.current === controller) {
        currentLoadControllerRef.current = null;
        setLoadingLeads(false);
        setIsInitialLoad(false);
        setIsRefreshing(false);
      }
    }
  }, [selectedStore, isInitialLoad, page, size, bodyTypeFilter, activeSearchQuery, startDate, endDate, setIsRefreshing]);

  // Keep ref updated with latest loadLeads function
  useEffect(() => {
    loadLeadsRef.current = loadLeads;
  }, [loadLeads]);

  const handleLeadSelect = useCallback(async (lead) => {
    // Open the lead's chat widget (or focus it if already open)
    openLead(lead);
    setFocusedLeadId(lead.id);

    // Opening a lead means its messages are now read — clear its unread flag
    setUnreadLeadIds((prev) => {
      if (!prev.has(lead.id)) return prev;
      const next = new Set(prev);
      next.delete(lead.id);
      return next;
    });
  }, [openLead]);

  const handleOutboundLeadCreated = useCallback((leadData) => {
    loadLeads();

    setTimeout(() => {
      handleLeadSelect(leadData);
    }, 500);
  }, [loadLeads, handleLeadSelect]);

  useEffect(() => {
    if (isAuthenticated && location.pathname === '/') {
      loadUserPreferences();
      loadEnterprises();
    } else if (isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, location.pathname, user?.role, user?.enterpriseIdFk]);
  // Keep a ref to the currently open lead IDs so the event-bus subscriber
  // below (set up once) can always see the latest value without re-subscribing.
  useEffect(() => {
    openLeadIdsRef.current = openLeadIds;
  }, [openLeadIds]);

  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated || deepLinkHandledRef.current) return;

    const linkTokenMatch = location.pathname.match(/^\/l\/([^/]+)\/?$/);
    const linkToken = linkTokenMatch ? linkTokenMatch[1] : null;
    const rawLeadId = new URLSearchParams(location.search).get('leadId');
    if (!linkToken && !rawLeadId) return;

    deepLinkHandledRef.current = true;
    const clearParam = () => navigate('/', { replace: true });

    const openLead = (leadId) => {
      if (!Number.isInteger(leadId) || leadId <= 0) {
        clearParam();
        return;
      }
      getLeadById(leadId)
        .then((lead) => {
          if (!lead || !lead.id) return;
          openLeadsCacheRef.current.set(lead.id, lead);
          setOpenLeadIds((prev) => {
            if (prev.includes(lead.id)) return prev;
            const next = [...prev, lead.id];
            return next.length > 3 ? next.slice(next.length - 3) : next;
          });
          setLastOpenedLeadId(lead.id);
          setFocusedLeadId(lead.id);
          setScrollToBottomOnLoad(true);
          setTimeout(() => setScrollToBottomOnLoad(false), 100);
        })
        .catch((err) => {
          console.error(`Failed to open lead ${leadId} from handoff link:`, err);
        })
        .finally(clearParam);
    };

    if (linkToken) {
      resolveLeadLink(linkToken)
        .then((res) => openLead(Number(res?.leadId)))
        .catch((err) => {
          // Expired/invalid/already-used link - nothing to open, just land on the dashboard.
          console.error('Failed to resolve handoff link token:', err);
          clearParam();
        });
      return;
    }

    openLead(Number(rawLeadId));
    // Deliberately keyed to `isAuthenticated` only - this is a one-shot deep-link
    // handler that must run once when the session becomes ready, not on every
    // location change.
  }, [isAuthenticated]);

 // Load leads and connect to the real-time event stream when a store is
  // selected. Replaces the old 30s poll: the initial load still happens
  // immediately, but after that the frontend stays idle and only refetches
  // when the backend actually pushes a NEW_MESSAGE event over SSE.
  useEffect(() => {
    if (selectedStore && isAuthenticated && location.pathname === '/') {
      // Reset initial load flag and pagination when store changes
      setIsInitialLoad(true);
      setPage(0);

      // Open (or move) the shared SSE connection to this store. Idempotent
      // for the same store id, so this is safe to call on every effect run.
      leadEventBus.connect(selectedStore);
    } else {
      leadEventBus.disconnect();
    }
  }, [selectedStore, isAuthenticated, location.pathname]);


  useEffect(() => {
    if (selectedStore && isAuthenticated && location.pathname === '/') {
      loadLeadsRef.current?.();
    }
  }, [selectedStore, isAuthenticated, location.pathname, page, size, bodyTypeFilter, activeSearchQuery, startDate, endDate, refreshToken]);

  // Subscribe once to the shared lead-event stream: on any new message,
  // refetch the leads list (so ordering/last-activity reflect it) and flag
  // the lead as unread unless its conversation is the one currently open.
  useEffect(() => {
    const unsubscribe = leadEventBus.subscribe((event) => {
      if (!event || event.type !== 'NEW_MESSAGE' || !event.leadId) {
        return;
      }

      if (currentPathRef.current !== '/') {
        return;
      }

      loadLeadsRef.current?.();

      if (openLeadIdsRef.current.includes(event.leadId)) {
        // Conversation is already open — ChatBox's own SSE subscription
        // will refetch it directly, no need to flag it as unread.
        return;
      }

      setUnreadLeadIds((prev) => {
        if (prev.has(event.leadId)) return prev;
        const next = new Set(prev);
        next.add(event.leadId);
        return next;
      });
    });

    return unsubscribe;
  }, []);

  const loadUserPreferences = async () => {
    try {
      const response = await authService.apiCall('/auth/preferences');
      if (response.ok) {
        const preferences = await response.json();
        
        const scopedUser = authService.isEnterpriseScopedUser(user);
        const enterpriseId = scopedUser ? user.enterpriseIdFk : preferences?.enterpriseIdFk;

        // Start from a clean selection so nothing from a previous session/enterprise survives.
        setSelectedEnterprise(enterpriseId || null);
        setSelectedBOC(null);
        setSelectedStore(null);
        setSelectedStoreAssistant(null);
        setBocs([]);
        setStores([]);
        setStoreAccessNotice(
          scopedUser && !enterpriseId
            ? 'Your account is not assigned to an enterprise yet. Please ask a super admin to assign one.'
            : null
        );

        // USER accounts always use the enterprise assigned to their account;
        // saved filters may only select a BOC/store inside that enterprise.
        const availableBOCs = enterpriseId ? await loadBOCs(enterpriseId) : [];
        const preferredBOC = availableBOCs?.some((boc) => boc.id === preferences?.bocIdFk)
          ? preferences.bocIdFk
          : null;
        if (preferredBOC) {
          setSelectedBOC(preferredBOC);
          const storesData = await loadStores(preferredBOC);
          
          // Select store from preferences if it exists and has assistant
          if (preferences?.storeIdFk && storesData) {
            const storeWithAssistant = storesData.find(s => s.id === preferences.storeIdFk && s.hasAssistant);
            if (storeWithAssistant) {
              setSelectedStore(preferences.storeIdFk);
              // Set assistant info
              setSelectedStoreAssistant({
                phoneNumber: storeWithAssistant.assistantPhoneNumber,
                firstName: storeWithAssistant.assistantFirstName,
                lastName: storeWithAssistant.assistantLastName,
                timeZone: storeWithAssistant.timeZone
              });
              // Initial preferences loaded
              if (isInitialLoad) {
                setIsInitialLoad(false);
              }
            } else {
              // Store doesn't have assistant, don't select it
              console.log('Store from preferences has no assistant, not selecting it');
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading user preferences:', err);
    }
  };

  const loadEnterprises = async () => {
    setLoadingEnterprises(true);
    setLoading(true);
    try {
      const response = await authService.apiCall('/admin/enterprises');
      if (response.ok) {
        const data = await response.json();
        setEnterprises(data);
      }
    } catch (err) {
      console.error('Error loading enterprises:', err);
    } finally {
      setLoadingEnterprises(false);
      setLoading(false);
    }
  };

  const loadBOCs = async (enterpriseId) => {
    setLoadingBOCs(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/bocs/enterprise/${enterpriseId}`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBocs(data);
        return data;
      }
    } catch (err) {
      console.error('Error loading BOCs:', err);
    } finally {
      setLoadingBOCs(false);
    }
  };

  const loadStores = async (bocId) => {
    setLoadingStores(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/admin/stores/boc/${bocId}`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStores(data);
        return data;
      }
    } catch (err) {
      console.error('Error loading stores:', err);
    } finally {
      setLoadingStores(false);
    }
  };

  const handleEnterpriseChange = async (enterpriseId) => {
    if (authService.isEnterpriseScopedUser(user)) return;
    setSelectedEnterprise(enterpriseId);
    setSelectedBOC(null);
    setSelectedStore(null);
    setBocs([]);
    setStores([]);
    if (enterpriseId) {
      await loadBOCs(enterpriseId);
    }
    // Don't save preferences until store is selected
  };

  const handleBOCChange = async (bocId) => {
    setSelectedBOC(bocId);
    setSelectedStore(null);
    setStores([]);
    if (bocId) {
      await loadStores(bocId);
    }
    // Don't save preferences until store is selected
  };

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize) => {
    setSize(newSize);
    setPage(0);
  }, []);

  const handleBodyTypeFilterChange = useCallback((newBodyTypeFilter) => {
    setBodyTypeFilter(newBodyTypeFilter);
    setPage(0);
  }, []);

  const handleSearchQueryChange = useCallback((newSearchQuery) => {
    searchQueryRef.current = newSearchQuery;
    setSearchQuery(newSearchQuery);
  }, []);

  const handleDateRangeChange = useCallback((newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, []);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setActiveSearchQuery(searchQueryRef.current);
    setPage(0);
    setRefreshToken((prev) => prev + 1);
  }, []);

  const handleStoreChange = async (storeId) => {
    setSelectedStore(storeId);
    // Set assistant info for selected store
    const selectedStoreData = stores.find(s => s.id === storeId);
    if (selectedStoreData && selectedStoreData.hasAssistant) {
      setSelectedStoreAssistant({
        phoneNumber: selectedStoreData.assistantPhoneNumber,
        firstName: selectedStoreData.assistantFirstName,
        lastName: selectedStoreData.assistantLastName,
        timeZone: selectedStoreData.timeZone
      });
    } else {
      setSelectedStoreAssistant(null);
    }
    // Only save preferences when store is selected
    await saveUserPreferences(selectedEnterprise, selectedBOC, storeId);
  };

  const selectedStoreName = useMemo(() => {
    const selectedStoreData = stores.find(s => s.id === selectedStore);
    return selectedStoreData ? selectedStoreData.storeId : null;
  }, [stores, selectedStore]);

  const selectedStoreTimeZone = useMemo(() => {
    const selectedStoreData = stores.find(s => s.id === selectedStore);
    return resolveTimeZone(selectedStoreData?.timeZone);
  }, [stores, selectedStore]);

  const saveUserPreferences = async (enterpriseId, bocId, storeId) => {
    try {
      await authService.apiCall('/auth/preferences', {
        method: 'PUT',
        body: JSON.stringify({
          enterpriseIdFk: enterpriseId,
          bocIdFk: bocId,
          storeIdFk: storeId
        })
      });
    } catch (err) {
      console.error('Error saving user preferences:', err);
    }
  };

  // Memoized no-op: ChatBox refetches itself via leadEventBus, so this only
  // exists to give React.memo's comparator a stable prop identity.
  const handleMessageSent = useCallback(() => {}, []);

  const handleDeleteLead = async (leadId) => {
    setShowDeleteConfirmModal(leadId);
  };

  const handleConfirmDeleteLead = async (leadId) => {
    setShowDeleteConfirmModal(null);

    try {
      setDeletingLeadId(leadId);

      const leadToDelete = leads.find((lead) => lead.id === leadId);
      if (
        leadToDelete?.appointment &&
        (
          leadToDelete.appointment.status === 'BOOKED' ||
          leadToDelete.appointment.status === 'RESCHEDULED'
        )
      ) {
        try {
          const cancelResponse = await fetch(
            `${getApiBaseUrl()}/appointments/${leadId}/cancel`,
            {
              method: 'DELETE',
              headers: authService.getAuthHeaders()
            }
          );

          if (cancelResponse.ok) {
            console.log('Appointment cancelled as part of lead deletion');
          }
        } catch (cancelError) {
          console.warn(cancelError);
        }
      }

      await deleteLead(leadId);

      closeLead(leadId);

      await loadLeads();
    } catch (err) {
      console.error('Error deleting lead:', err);
      setError('Failed to delete lead');
    } finally {
      setDeletingLeadId(null);
    }
  };

  const handleCancelAppointment = async (leadId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    setCancellingAppointmentId(leadId);
    setCancelAppointmentError(null);

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/appointments/${leadId}/cancel`,
        {
          method: 'DELETE',
          headers: authService.getAuthHeaders()
        }
      );

      if (response.ok) {
        await loadLeads();
      } else {
        const errorData = await response.json();

        // Scoped to this lead and shown inline by ChatWidget - see the state
        // declaration above for why this must not go through setError.
        setCancelAppointmentError({
          leadId,
          message: errorData.message || 'Failed to cancel appointment'
        });
      }
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      setCancelAppointmentError({ leadId, message: 'Failed to cancel appointment' });
    } finally {
      setCancellingAppointmentId(null);
    }
  };

  const handleManualAppointmentAction = async (leadId, payload) => {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/appointments/${leadId}/manual-action`,
        {
          method: 'POST',
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        try {
          const updatedLead = await getLeadById(leadId);
          if (updatedLead?.id) {
            openLeadsCacheRef.current.set(updatedLead.id, updatedLead);
            setLeads((currentLeads) => currentLeads.map((lead) => (
              lead.id === updatedLead.id ? updatedLead : lead
            )));
          }
        } catch (refreshError) {
          console.error('Error refreshing booked lead:', refreshError);
        }

        return { ok: true, message: data.message };
      }

      if (response.status === 409) {
        await loadLeads();
        return {
          ok: false,
          message: data.message || 'This appointment changed - refreshed, please try again.',
        };
      }

      // 422 carries Ztime's own rejection text, which usually names the bad field.
      return { ok: false, message: data.message || 'Failed to process the appointment action.' };
    } catch (err) {
      console.error('Error running manual appointment action:', err);
      return { ok: false, message: 'Failed to reach the server.' };
    }
  };

  const handleUpdateLeadDetails = async (leadId, payload) => {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/leads/${leadId}/details`,
        {
          method: 'PUT',
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        await loadLeads();
        return { ok: true, message: data.message };
      }

      return { ok: false, message: data.message || 'Failed to update lead details.' };
    } catch (err) {
      console.error('Error updating lead details:', err);
      return { ok: false, message: 'Failed to reach the server.' };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';

      case 'contacted':
        return 'bg-yellow-100 text-yellow-800';

      case 'qualified':
        return 'bg-green-100 text-green-800';

      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAppointmentStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-orange-100 text-orange-800';

      case 'BOOKED':
        return 'bg-green-100 text-green-800';

      case 'RESCHEDULED':
        return 'bg-blue-100 text-blue-800';

      case 'CANCELED':
        return 'bg-red-100 text-red-800';

      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md mx-auto text-center p-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">
              Connection Error
            </h2>

            <p className="text-red-600 mb-4">{error}</p>

            <button
              onClick={loadLeads}
              className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
            >
              Retry Connection
            </button>

            <button
              onClick={handleLogout}
              className="mt-2 ml-2 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  const dashboardElement = (
      <>
        <div
          className={`flex flex-col h-screen bg-gray-100 overflow-hidden ${
            isResizing ? 'cursor-col-resize select-none' : ''
          }`}
        >

          {/* TOP BAR */}
          <div className="flex items-center gap-2 sm:gap-3">
            {!sidebarOpen && (
              <button
                onClick={() => setSidebarOpen(true)}
                title="Open sidebar"
                aria-label="Open sidebar"
                aria-expanded={false}
                className="-ml-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-700 transition-colors duration-150 hover:bg-gray-100/70 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
              >
                <PanelLeft size={17} strokeWidth={1.5} />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <AppHeader
                user={user}
                onLogout={handleLogout}
                badgeCount={totalElements}
                badgeLabel="Leads"
              />
            </div>
          </div>

          {/* BODY */}
          <div className="flex flex-1 overflow-hidden relative">

            {/* Mobile backdrop - tap to close the sidebar drawer */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 bg-black/40 z-30 md:hidden"
                onClick={() => setSidebarOpen(false)}
                aria-hidden="true"
              />
            )}

            {/* LEFT SIDEBAR - Store Selection */}
            <div
              className={`bg-white flex-shrink-0 overflow-y-auto overflow-x-hidden fixed md:relative inset-y-0 md:inset-y-auto left-0 md:left-auto top-0 md:top-auto z-40 md:z-auto shadow-2xl md:shadow-none ${
                isResizing ? '' : 'transition-[width] duration-300 ease-in-out'
              }`}
              style={{ width: sidebarOpen ? sidebarWidth : 0, maxWidth: sidebarOpen ? '85vw' : undefined }}
            >
              <div className="p-4" style={{ width: sidebarWidth, maxWidth: '85vw' }}>

                {/* Lead Action Buttons */}
                {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                  <div className="mb-4 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setShowOutboundModal(true)}
                      className="flex items-center justify-center px-3 py-2 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 transition-colors"
                    >
                      <MessageSquarePlus size={14} className="mr-1" />
                      Start Outbound
                    </button>
                    <button
                      onClick={() => setShowTriggerInboundModal(true)}
                      className="flex items-center justify-center px-3 py-2 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 transition-colors"
                    >
                      <MessageSquare size={14} className="mr-1 text-gray-500" />
                      Trigger Inbound
                    </button>
                  </div>
                )}

                {/* Store Selection */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 flex items-center">
                      <Building className="h-4 w-4 mr-2" />
                      Store Selection
                    </span>
                    <button
                      onClick={() => setSidebarOpen(false)}
                      title="Close sidebar"
                      aria-label="Close sidebar"
                      className="-mr-1 flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:bg-gray-200/70 hover:text-gray-600"
                    >
                      <PanelLeft size={14} strokeWidth={1.5} />
                    </button>
                  </div>

                  <div className="p-4 space-y-3">
                      {storeAccessNotice && (
                        <div role="alert" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                          {storeAccessNotice}
                        </div>
                      )}
                      <EnterpriseSelector
                        locked={authService.isEnterpriseScopedUser(user)}
                        enterprises={enterprises}
                        selectedEnterprise={selectedEnterprise}
                        onChange={handleEnterpriseChange}
                      />

                      <div className="relative">
                        <label className="block text-xs font-medium text-gray-700 mb-1">BOC</label>
                        <select
                          value={selectedBOC || ''}
                          onChange={(e) => handleBOCChange(e.target.value ? Number(e.target.value) : null)}
                          disabled={!selectedEnterprise}
                          className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Select BOC</option>
                          {loadingBOCs ? (
                            <option disabled>Loading...</option>
                          ) : (
                            bocs.map((boc) => (
                              <option key={boc.id} value={boc.id}>
                                {boc.bocId}
                              </option>
                            ))
                          )}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-5">
                          {loadingBOCs ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                      </div>

                      <div className="relative">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Store</label>
                        <select
                          value={selectedStore || ''}
                          onChange={(e) => handleStoreChange(e.target.value ? Number(e.target.value) : null)}
                          disabled={!selectedBOC}
                          className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg shadow-sm appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Select Store</option>
                          {loadingStores ? (
                            <option disabled>Loading...</option>
                          ) : (
                            stores.map((store) => (
                              <option
                                key={store.id}
                                value={store.id}
                                disabled={!store.hasAssistant}
                              >
                                {store.name || store.storeId}{!store.hasAssistant ? ' (No Assistant)' : ''}
                              </option>
                            ))
                          )}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 mt-5">
                          {loadingStores ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                      </div>
                    </div>
                </div>

                {/* Assistant Details */}
                {selectedStoreAssistant && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <span className="text-purple-700 text-base font-semibold">
                            {selectedStoreAssistant.firstName.charAt(0)}{selectedStoreAssistant.lastName.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 flex-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {selectedStoreAssistant.firstName} {selectedStoreAssistant.lastName}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center mt-1">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {selectedStoreAssistant.phoneNumber}
                        </p>
                        {(() => {
                          const colors = getTimeZoneColor(selectedStoreAssistant.timeZone);
                          return (
                            <div className={`inline-flex items-center gap-1.5 mt-2 px-2 py-1 rounded-full text-[11px] font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
                              <Clock size={12} className={colors.icon} />
                              <span>{formatTimeZoneLabel(selectedStoreAssistant.timeZone)}</span>
                              <span className="opacity-60">·</span>
                              <LiveZoneClock timeZone={selectedStoreAssistant.timeZone} showIcon={false} className={colors.text} />
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* DIVIDER - click to collapse, drag to resize */}
            <div
              onMouseDown={handleDividerMouseDown}
              onClick={handleDividerClick}
              role="separator"
              aria-orientation="vertical"
              className={`group relative w-px flex-shrink-0 transition-colors duration-150 ${
                sidebarOpen ? 'cursor-col-resize' : 'cursor-pointer'
              } ${
                isResizing ? 'bg-blue-400' : 'bg-gray-200 hover:bg-blue-400'
              }`}
            >
              {/* invisible but generous hit area over the 1px line */}
              <div className="absolute inset-y-0 -left-2 -right-2 z-10" />

              {/* hover hint */}
              <div className="pointer-events-none absolute left-4 top-1/2 z-30 -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-800 px-3 py-2 opacity-0 shadow-lg transition-opacity delay-200 duration-150 group-hover:opacity-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-white">
                    {sidebarOpen ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <span className="rounded border border-gray-600 px-1 text-[10px] leading-4 text-gray-400">Ctrl B</span>
                </div>
                {sidebarOpen && (
                  <div className="mt-0.5 text-[11px] text-gray-400">Drag to resize</div>
                )}
              </div>
            </div>

            {/* RIGHT CONTENT - Leads Table */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 min-w-0">
              {loadingLeads ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  <span className="ml-3 text-gray-600">Loading leads...</span>
                </div>
              ) : selectedStore ? (
                <LeadsTable
                  leads={leads}
                  onLeadSelect={handleLeadSelect}
                  selectedLeadIds={openLeadIds}
                  unreadLeadIds={unreadLeadIds}
                  page={page}
                  size={size}
                  totalElements={totalElements}
                  totalPages={totalPages}
                  bodyTypeFilter={bodyTypeFilter}
                  searchQuery={searchQuery}
                  startDate={startDate}
                  endDate={endDate}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  onBodyTypeFilterChange={handleBodyTypeFilterChange}
                  onSearchQueryChange={handleSearchQueryChange}
                  onDateRangeChange={handleDateRangeChange}
                  onRefresh={handleRefresh}
                  isLoading={isRefreshing}
                  storeTimeZone={selectedStoreTimeZone}
                />
              ) : (
                <div className="flex items-center justify-center py-16 text-center">
                  <div>
                    <Building size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Store</h3>
                    <p className="text-gray-600">Choose an enterprise, BOC and store on the left to view leads.</p>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* CHAT WIDGETS - up to 3 floating, closeable widgets tiled along the
            bottom-right, one per open conversation. */}
        {openLeads.map((lead, index) => (
          <ChatWidget
            key={lead.id}
            selectedLead={lead}
            selectedLeadId={lead.id}
            storeTimeZone={lead.timeZone || selectedStoreTimeZone}
            rightOffsetPx={widgetRightOffset(index)}
            isFocused={focusedLeadId === lead.id}
            onFocusWidget={setFocusedLeadId}
            onWidthChange={handleWidgetWidthChange}
            onClose={() => closeLead(lead.id)}
            scrollToBottomOnLoad={scrollToBottomOnLoad && lead.id === lastOpenedLeadId}
            onMessageSent={handleMessageSent}
            onDeleteLead={handleDeleteLead}
            deletingLeadId={deletingLeadId}
            onCancelAppointment={handleCancelAppointment}
            cancelAppointmentError={cancelAppointmentError}
            onDismissCancelAppointmentError={() => setCancelAppointmentError(null)}
            onManualAppointmentAction={handleManualAppointmentAction}
            onUpdateLeadDetails={handleUpdateLeadDetails}
            user={user}
            awaitingAiReplySince={awaitingAiReplyByLead[lead.id] || null}
            onAiReplyWaitDone={() => clearAwaitingAiReply(lead.id)}
          />
        ))}

        {/* FIXED SECTION */}
        <OutboundLeadModal
          isOpen={showOutboundModal}
          onClose={() => setShowOutboundModal(false)}
          onLeadCreated={handleOutboundLeadCreated}
          selectedStore={selectedStore}
          selectedStoreAssistant={selectedStoreAssistant}
        />

        <TriggerInboundModal
          isOpen={showTriggerInboundModal}
          onClose={() => setShowTriggerInboundModal(false)}
          onConversationTriggered={(response) => {
            setShowTriggerInboundModal(false);
            setAwaitingAiReplyByLead((prev) => ({ ...prev, [response.leadId]: Date.now() }));
            loadLeads().then(() => openLead({ id: response.leadId }));
          }}
          selectedStore={selectedStore}
        />

      </>
  );

  return (
    <>
      <Routes>
          <Route path="/" element={dashboardElement} />

        {/* Handoff-email deep link target: `/l/<token>` - handled by the
            deep-link effect above, which reads `location.pathname` itself, so
            this route just needs to render the same dashboard rather than
            falling through to the `*` -> `/` redirect below. */}
        <Route path="/l/:token" element={dashboardElement} />

        {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
          <Route
            path="/gi"
            element={
              <GIDashboard
                user={user}
                onLogout={handleLogout}
                unreadLeadIds={unreadLeadIds}
                setUnreadLeadIds={setUnreadLeadIds}
                defaultEnterprise={selectedEnterprise}
              />
            }
          />
        )}

        {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
          <Route
            path="/appointments"
            element={
              <AppointmentsManagement
                user={user}
                onLogout={handleLogout}
                defaultEnterprise={selectedEnterprise}
                defaultBOC={selectedBOC}
                defaultStore={selectedStore}
              />
            }
          />
        )}

        {(user?.role === 'SUPER_ADMIN' || (user?.role === 'ADMIN' && isDevEnvironment())) && (
          <Route
            path="/appointments-v2"
            element={
              <AppointmentsV2
                user={user}
                onLogout={handleLogout}
                defaultEnterprise={selectedEnterprise}
                defaultBOC={selectedBOC}
                defaultStore={selectedStore}
              />
            }
          />
        )}

        {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
          <>
            <Route path="/admin" element={<Admin defaultStore={selectedStore} />} />

            <Route
              path="/admin/categories"
              element={<LeadCategoryManagement />}
            />

            <Route
              path="/admin/uploads"
              element={<FileUploadManagement />}
            />

            <Route
              path="/admin/monitoring"
              element={<Monitoring user={user} onLogout={handleLogout} />}
            />
          </>
        )}

        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-5">
              <h3 className="text-lg font-bold text-white">Delete Lead</h3>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-700 text-sm leading-relaxed">
                Are you sure you want to delete this lead and all related data?
              </p>
              <p className="text-red-600 text-xs font-semibold mt-3">
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setShowDeleteConfirmModal(null)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirmDeleteLead(showDeleteConfirmModal)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
